/**
 * Multi-select control for picking PEOPLE on a form.
 *
 * Staff-bound fields (Staff Attendees, etc.) draw their options from the org's
 * live staff list, which is unbounded — an org with 40 promotoras rendered as a
 * checkbox group is 40 rows of scrolling before you reach the next question.
 * This is the control that ask produced: a closed trigger showing who is
 * selected, and a searchable panel to change it.
 *
 * Deliberately NOT portaled to document.body. The custom ui/select does that to
 * escape modal overflow clipping, and it cost us a bug — a Radix Dialog sets
 * `pointer-events: none` on <body>, the portaled menu inherits it, and options
 * stop being clickable (FE #465). This panel is absolutely positioned inside
 * its own relatively-positioned wrapper, so it is unaffected by anything the
 * surrounding dialog does to the body.
 *
 * Values are user ids as strings. A value with no matching option is still
 * shown as a chip using its raw text, so the free-text names promotoras typed
 * before this field became a picker stay visible and removable instead of
 * silently disappearing from the form.
 */
import React from 'react';

export type StaffMultiSelectOption = { label: string; value: string };

interface StaffMultiSelectProps {
  options: StaffMultiSelectOption[];
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  hasError?: boolean;
  placeholder?: string;
  /** Rendered when the org staff list is still loading or came back empty. */
  emptyHint?: string;
  /** Accessible name for the control (callers pass a translated label). */
  ariaLabel?: string;
  /** Overrides for the panel's own text, so callers can translate it. */
  searchPlaceholder?: string;
  clearLabel?: string;
  noMatchLabel?: (query: string) => string;
  removeLabel?: (label: string) => string;
  /** Sort options by label (default). False keeps the caller's order. */
  sort?: boolean;
}

/**
 * Coerce whatever is stored into a string[].
 *
 * This field has been a free-text box in production, so stored values are a
 * mix: a real array, a JSON-encoded array, or a comma-separated string someone
 * typed by hand. All three have to survive being opened in the new control.
 */
export function toStringArray(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map((v) => String(v)).filter(Boolean);
  if (raw == null) return [];
  const s = String(raw).trim();
  if (!s) return [];
  if (s.startsWith('[')) {
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) return parsed.map((v) => String(v)).filter(Boolean);
    } catch {
      /* fall through to the comma split below */
    }
  }
  return s
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

export const StaffMultiSelect: React.FC<StaffMultiSelectProps> = ({
  options,
  value,
  onChange,
  disabled = false,
  hasError = false,
  placeholder = 'Select staff…',
  emptyHint = 'No staff available',
  ariaLabel,
  searchPlaceholder = 'Search staff…',
  clearLabel = 'Clear all',
  noMatchLabel = (q: string) => `No match for “${q}”`,
  removeLabel = (label: string) => `Remove ${label}`,
  sort = true,
}) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const searchRef = React.useRef<HTMLInputElement | null>(null);

  const selected = React.useMemo(() => toStringArray(value), [value]);

  const labelFor = React.useCallback(
    (v: string) => options.find((o) => o.value === v)?.label ?? v,
    [options],
  );

  // Close on outside click and on Escape.
  React.useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  React.useEffect(() => {
    if (open) searchRef.current?.focus();
    else setQuery('');
  }, [open]);

  const toggle = (v: string) => {
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  };

  const visible = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const sorted = sort ? [...options].sort((a, b) => a.label.localeCompare(b.label)) : options;
    return q ? sorted.filter((o) => o.label.toLowerCase().includes(q)) : sorted;
  }, [options, query, sort]);

  return (
    <div className="staff-multiselect" ref={wrapperRef}>
      {/* A div, not a <button>: the chips carry their own remove buttons, and
          nesting a button inside a button is invalid HTML (and made the two
          ambiguous to assistive tech and to tests alike). */}
      <div
        role="combobox"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-disabled={disabled || undefined}
        aria-label={ariaLabel}
        className={
          `staff-multiselect__trigger${hasError ? ' staff-multiselect__trigger--error' : ''}` +
          (disabled ? ' staff-multiselect__trigger--disabled' : '')
        }
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
      >
        <span className="staff-multiselect__chips">
          {selected.length === 0 ? (
            <span className="staff-multiselect__placeholder">{placeholder}</span>
          ) : (
            selected.map((v) => (
              <span key={v} className="staff-multiselect__chip">
                {labelFor(v)}
                {!disabled && (
                  <button
                    type="button"
                    aria-label={removeLabel(labelFor(v))}
                    className="staff-multiselect__chip-remove"
                    onClick={(e) => {
                      // Don't let the click bubble to the trigger and reopen.
                      e.stopPropagation();
                      onChange(selected.filter((x) => x !== v));
                    }}
                  >
                    ×
                  </button>
                )}
              </span>
            ))
          )}
        </span>
        <span className="staff-multiselect__caret" aria-hidden="true">
          ▾
        </span>
      </div>

      {open && (
        <div className="staff-multiselect__panel" role="listbox" aria-multiselectable="true">
          <input
            ref={searchRef}
            type="text"
            className="staff-multiselect__search"
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          {options.length === 0 && <div className="staff-multiselect__empty">{emptyHint}</div>}
          {options.length > 0 && visible.length === 0 && (
            <div className="staff-multiselect__empty">{noMatchLabel(query)}</div>
          )}

          <div className="staff-multiselect__options">
            {visible.map((opt) => {
              const isSelected = selected.includes(opt.value);
              return (
                <label key={opt.value} className="staff-multiselect__option" role="option" aria-selected={isSelected}>
                  <input type="checkbox" checked={isSelected} onChange={() => toggle(opt.value)} />
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>

          {selected.length > 0 && (
            <button type="button" className="staff-multiselect__clear" onClick={() => onChange([])}>
              {clearLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default StaffMultiSelect;
